<template>
  <div
    class="week-roster-grid"
    :class="{ 'week-roster-grid--menu-open': openMenuJobId !== null }"
  >
    <div v-if="workers.length === 0" class="roster-empty">
      <p>No workers found.</p>
    </div>

    <div v-else>
      <!-- Below xl: stacked cards — one per worker, days listed vertically
           (two cards per row on tablets). Same data + interactions as the
           desktop grid, with no horizontal scrolling at any width. -->
      <div class="d-xl-none roster-mobile-list">
        <div
          v-for="worker in workers"
          :key="`m-${worker.id}`"
          class="roster-mobile-card"
        >
          <div class="roster-mobile-worker">
            <b-avatar
              size="2.2rem"
              variant="primary"
              :src="workerAvatarUrl(worker)"
              :text="initials(worker.full_name)"
              class="worker-avatar"
            />
            <div class="worker-copy">
              <div class="worker-name">{{ worker.full_name }}</div>
              <b-badge v-if="worker.trade" variant="light" class="worker-trade">
                {{ formatTrade(worker.trade) }}
              </b-badge>
            </div>
          </div>

          <div v-for="date in weekDates" :key="date" class="roster-mobile-day">
            <div class="roster-mobile-day-label">
              <div class="day-heading-weekday">
                {{ formatDayHeader(date).weekday }}
              </div>
              <div class="day-heading-date">
                {{ formatDayHeader(date).dayLabel }}
              </div>
            </div>
            <div
              class="roster-mobile-day-content"
              :class="{
                'roster-cell--clickable':
                  canManage && cellJobs(worker.id, date).length === 0,
              }"
              @click="onCellClick(worker.id, date)"
            >
              <div
                v-if="cellJobs(worker.id, date).length === 0"
                class="roster-cell-off roster-cell-off--mobile"
              >
                OFF
              </div>
              <div v-else class="roster-chip-list">
                <div
                  v-for="job in cellJobs(worker.id, date)"
                  :key="job.id"
                  class="roster-chip"
                  :class="{
                    'roster-chip--night': isOvernight(job),
                    'roster-chip--cancelled': job.status === 'cancelled',
                    'roster-chip--completed': job.status === 'completed',
                    'roster-chip--menu-open': openMenuJobId === job.id,
                  }"
                  @click.stop="$emit('chip-click', { job })"
                >
                  <div class="roster-chip-title-row">
                    <div class="roster-chip-title">
                      <div>{{ job.title }}</div>
                      <span
                        v-if="job.status === 'cancelled'"
                        class="roster-chip-cancelled-badge"
                      >
                        Cancelled
                      </span>
                      <span
                        v-if="job.status === 'completed'"
                        class="roster-chip-completed-badge"
                      >
                        Completed
                      </span>
                      <span
                        v-if="isOvernight(job)"
                        class="roster-chip-night-badge"
                      >
                        Night
                      </span>
                      <span
                        v-if="job.recurrence_group_id"
                        class="roster-chip-series-badge"
                      >
                        Series
                      </span>
                      <div v-if="formatChipTime(job)" class="roster-chip-time">
                        {{ formatChipTime(job) }}
                      </div>
                    </div>
                    <job-actions-menu
                      v-if="canManage"
                      :job="job"
                      class="roster-chip-actions"
                      @make-repeat="$emit('chip-make-repeat', { job })"
                      @done="$emit('chip-action-done', $event)"
                      @menu-toggle="onChipMenuToggle(job.id, $event)"
                    />
                  </div>
                  <div
                    v-if="job.site_name || job.address"
                    class="roster-chip-sub"
                  >
                    {{
                      [job.site_name, job.address].filter(Boolean).join(' · ')
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="roster-table-wrap d-none d-xl-block">
        <table class="table mb-0 roster-table">
          <thead>
            <tr>
              <th class="worker-heading">Worker</th>
              <th v-for="date in weekDates" :key="date" class="day-heading">
                <div class="day-heading-weekday">
                  {{ formatDayHeader(date).weekday }}
                </div>
                <div class="day-heading-date">
                  {{ formatDayHeader(date).dayLabel }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="worker in workers" :key="worker.id">
              <td class="worker-cell">
                <div class="worker-summary">
                  <b-avatar
                    size="2.5rem"
                    variant="primary"
                    :src="workerAvatarUrl(worker)"
                    :text="initials(worker.full_name)"
                    class="worker-avatar"
                  />
                  <div class="worker-copy">
                    <div class="worker-name">{{ worker.full_name }}</div>
                    <b-badge
                      v-if="worker.trade"
                      variant="light"
                      class="worker-trade"
                    >
                      {{ formatTrade(worker.trade) }}
                    </b-badge>
                  </div>
                </div>
              </td>
              <td
                v-for="date in weekDates"
                :key="date"
                class="roster-cell"
                :class="{
                  'roster-cell--empty': cellJobs(worker.id, date).length === 0,
                  'roster-cell--clickable':
                    canManage && cellJobs(worker.id, date).length === 0,
                }"
                @click="onCellClick(worker.id, date)"
              >
                <div
                  v-if="cellJobs(worker.id, date).length === 0"
                  class="roster-cell-off"
                >
                  OFF
                </div>
                <div v-else class="roster-chip-list">
                  <div
                    v-for="job in cellJobs(worker.id, date)"
                    :key="job.id"
                    class="roster-chip"
                    :class="{
                      'roster-chip--night': isOvernight(job),
                      'roster-chip--cancelled': job.status === 'cancelled',
                      'roster-chip--completed': job.status === 'completed',
                      'roster-chip--menu-open': openMenuJobId === job.id,
                    }"
                    @click.stop="$emit('chip-click', { job })"
                  >
                    <div class="roster-chip-title-row">
                      <div class="roster-chip-title">
                        <div>{{ job.title }}</div>
                        <span
                          v-if="job.status === 'cancelled'"
                          class="roster-chip-cancelled-badge"
                        >
                          Cancelled
                        </span>
                        <span
                          v-if="job.status === 'completed'"
                          class="roster-chip-completed-badge"
                        >
                          Completed
                        </span>
                        <span
                          v-if="isOvernight(job)"
                          class="roster-chip-night-badge"
                        >
                          Night
                        </span>
                        <span
                          v-if="job.recurrence_group_id"
                          class="roster-chip-series-badge"
                        >
                          Series
                        </span>
                        <div
                          v-if="formatChipTime(job)"
                          class="roster-chip-time"
                        >
                          {{ formatChipTime(job) }}
                        </div>
                      </div>
                      <job-actions-menu
                        v-if="canManage"
                        :job="job"
                        class="roster-chip-actions"
                        @make-repeat="$emit('chip-make-repeat', { job })"
                        @done="$emit('chip-action-done', $event)"
                        @menu-toggle="onChipMenuToggle(job.id, $event)"
                      />
                    </div>
                    <div
                      v-if="job.site_name || job.address"
                      class="roster-chip-sub"
                    >
                      {{
                        [job.site_name, job.address].filter(Boolean).join(' · ')
                      }}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDayHeader } from '@/utils/weekHelpers'
import { avatarUrl } from '@/utils/avatar'
import { formatTrade } from '@/utils/timesheetHelpers'
import JobActionsMenu from '@/components/schedule/JobActionsMenu.vue'

export default {
  name: 'WeekRosterGrid',

  components: { JobActionsMenu },

  props: {
    workers: {
      type: Array,
      default: () => [],
    },
    weekDates: {
      type: Array,
      default: () => [],
    },
    jobsByWorkerAndDate: {
      type: Object,
      default: () => ({}),
    },
    canManage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Tracks which job's ⋯ menu is open so that chip can be lifted above
      // its siblings — otherwise a later chip in the same day cell paints
      // over an earlier one's open menu regardless of the menu's own
      // z-index, since neither chip establishes its own stacking context.
      openMenuJobId: null,
    }
  },

  methods: {
    formatDayHeader,
    formatTrade,

    onChipMenuToggle(jobId, isOpen) {
      this.openMenuJobId = isOpen ? jobId : null
    },

    cellJobs(workerId, date) {
      return this.jobsByWorkerAndDate[`${workerId}_${date}`] || []
    },

    workerAvatarUrl(worker) {
      return avatarUrl(worker.avatar)
    },

    initials(str) {
      if (!str) return '?'
      return str
        .match(/(^\S\S?|\b\S)?/g)
        .join('')
        .match(/(^\S|\S$)?/g)
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    onCellClick(workerId, date) {
      if (!this.canManage) return
      if (this.cellJobs(workerId, date).length > 0) return
      this.$emit('cell-click', { workerId, date })
    },

    formatChipTime(job) {
      if (!job.start_time && !job.end_time) return ''
      const range = `${job.start_time || '?'}–${job.end_time || '?'}`
      return this.isOvernight(job) ? `${range} (+1)` : range
    },

    // Prefer the API's derived flag; fall back to comparing the times locally
    // so the marker still shows against an API that predates the field.
    isOvernight(job) {
      if (typeof job.is_overnight === 'boolean') return job.is_overnight
      if (!job.start_time || !job.end_time) return false
      return job.end_time < job.start_time
    },
  },
}
</script>

<style scoped>
.week-roster-grid {
  background: #ffffff;
}

.roster-empty {
  padding: 3rem 1.5rem;
  color: #9ca3af;
  text-align: center;
}

.roster-empty p {
  margin: 0;
  font-size: 0.9rem;
}

.roster-table-wrap {
  background: #f8fafc;
}

.roster-table {
  /* Fluid: the worker column is fixed and the 7 day columns share the rest,
     so the grid always fits its container — no horizontal scrolling. */
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
}

.roster-table th,
.roster-table td {
  border-top: 0;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.roster-table th:last-child,
.roster-table td:last-child {
  border-right: 0;
}

.roster-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 0.85rem 0.75rem;
  background: #f3f4f6;
  color: #374151;
  text-align: center;
}

.worker-heading {
  width: 240px;
  text-align: left !important;
}

/* Day columns intentionally have no fixed width: with table-layout fixed
   they split the space left over by the worker column evenly. */

.day-heading-weekday {
  color: #111827;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
}

.day-heading-date {
  margin-top: 0.15rem;
  color: #6b7280;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.2;
}

.worker-cell {
  width: 240px;
  padding: 0.9rem 0.85rem;
  background: #ffffff;
  vertical-align: middle;
}

.worker-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.worker-avatar {
  flex-shrink: 0;
  box-shadow: 0 0 0 3px #eff6ff;
}

.worker-copy {
  min-width: 0;
}

.worker-name {
  overflow: hidden;
  color: #111827;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.worker-trade {
  display: block;
  max-width: 100%;
  margin-top: 0.25rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #6b7280;
  font-size: 0.65rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.roster-cell {
  min-height: 72px;
  padding: 0.65rem;
  background: #ffffff;
  vertical-align: top;
}

.roster-cell--empty {
  cursor: default;
}

.roster-cell--clickable {
  cursor: pointer;
  transition: background 0.15s;
}

.roster-cell--clickable:hover {
  background: #eff6ff;
}

.roster-cell--clickable:hover .roster-cell-off {
  border-color: #bfdbfe;
  background: #dbeafe;
  color: #1d4ed8;
}

.roster-cell-off {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  color: #9ca3af;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.roster-chip-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.roster-chip {
  position: relative;
  z-index: 1;
  display: block;
  padding: 0.55rem 0.65rem;
  border: 1px solid #dbeafe;
  border-left: 3px solid #3b82f6;
  border-radius: 8px;
  background: #eff6ff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.roster-chip:hover {
  border-color: #93c5fd;
  box-shadow: 0 6px 14px rgba(37, 99, 235, 0.12);
  transform: translateY(-1px);
}

/* Overnight shifts read as night work at a glance. Declared before the
   cancelled rules deliberately: a cancelled night shift must still read as
   cancelled, so equal-specificity source order lets grey win the chip while
   the Night badge stays visible. */
.roster-chip--night {
  border-color: #c7d2fe;
  border-left-color: #6366f1;
  background: #eef2ff;
}

.roster-chip--night:hover {
  border-color: #a5b4fc;
  box-shadow: 0 6px 14px rgba(99, 102, 241, 0.12);
}

.roster-chip-night-badge {
  display: inline-block;
  margin-top: 0.2rem;
  padding: 0.05rem 0.4rem;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-decoration: none;
  text-transform: uppercase;
}

.roster-chip--completed {
  border-color: #bbf7d0;
  border-left-color: #22c55e;
  background: #f0fdf4;
}

.roster-chip--completed:hover {
  border-color: #86efac;
  box-shadow: 0 6px 14px rgba(34, 197, 94, 0.12);
}

.roster-chip-completed-badge {
  display: inline-block;
  margin-top: 0.2rem;
  padding: 0.05rem 0.4rem;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.roster-chip--cancelled {
  border-color: #e5e7eb;
  border-left-color: #9ca3af;
  background: #f3f4f6;
}

.roster-chip--cancelled:hover {
  border-color: #d1d5db;
  box-shadow: 0 6px 14px rgba(107, 114, 128, 0.12);
}

.roster-chip--cancelled .roster-chip-title,
.roster-chip--cancelled .roster-chip-time {
  color: #6b7280;
  text-decoration: line-through;
}

.roster-chip-cancelled-badge {
  display: inline-block;
  margin-top: 0.2rem;
  padding: 0.05rem 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-decoration: none;
  text-transform: uppercase;
}

/* Marks a job that belongs to a recurring series (job.recurrence_group_id) so
   it can be told apart from a one-off job without opening its ⋯ menu. Amber
   rather than the status colors (indigo/green/grey above) so it never reads
   as a status. */
.roster-chip-series-badge {
  display: inline-block;
  margin-top: 0.2rem;
  padding: 0.05rem 0.4rem;
  border: 1px solid #fde68a;
  border-radius: 999px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-decoration: none;
  text-transform: uppercase;
}

/* While its ⋯ menu is open, lift the chip above every sibling chip in the
   same day cell — a chip further down the flex list is otherwise a later,
   equally-unstacked sibling and paints over the open dropdown regardless of
   the dropdown's own z-index. */
.roster-chip--menu-open {
  z-index: 30;
}

/* Hovering a chip the open menu overlaps re-triggers its hover lift, and
   `transform` starts a stacking context of its own — that context then
   competes with the open menu's z-index instead of just losing to it,
   which is what made the menu flicker behind the job under the cursor.
   Freeze the lift on every chip, INCLUDING the one whose own menu is open
   (the cursor sits on that exact chip while using its menu, so excluding
   it left the fight in place for the most common case), for as long as
   any menu in the grid is open. */
.week-roster-grid--menu-open .roster-chip:hover {
  transform: none;
}

.roster-chip-sub {
  margin-top: 0.35rem;
  color: #4b5563;
  font-size: 0.68rem;
  line-height: 1.25;
  white-space: normal;
  word-break: break-word;
}

/* Only the title row clears the ⋯ trigger — padding the whole chip squeezed
   long job titles into a one-word-per-line column. */
.roster-chip-title-row {
  padding-right: 1.5rem;
}

/* The ⋯ trigger sits top-right of the chip and is ALWAYS rendered, never
   hover-revealed: the stacked card layout below xl has no hover at all, so a
   control that only appears on hover is invisible to anyone on a phone. */
.roster-chip-actions {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
}

.roster-table tbody tr:nth-child(even) .worker-cell,
.roster-table tbody tr:nth-child(even) .roster-cell {
  background: #fcfcfd;
}

.roster-table tbody tr:hover .worker-cell,
.roster-table tbody tr:hover .roster-cell {
  background: #f8fafc;
}

/* ── Phones (< md): stacked worker cards ─────────────────────────────
   One card per worker; the week runs vertically inside it. Fits 320px
   with no horizontal scroll; the md+ grid is untouched. */
.roster-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8fafc;
}

/* Tablets (576px–xl): two cards per row so the width isn't wasted. */
@media (min-width: 576px) {
  .roster-mobile-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }
}

.roster-mobile-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
}

.roster-mobile-worker {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.8rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.roster-mobile-day {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #f3f4f6;
}

.roster-mobile-day:last-child {
  border-bottom: 0;
}

.roster-mobile-day-label {
  flex: 0 0 64px;
  padding: 0.55rem 0.5rem 0.55rem 0.8rem;
}

.roster-mobile-day-content {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.5rem 0.8rem 0.5rem 0;
}

.roster-cell-off--mobile {
  min-height: 36px;
  font-size: 0.62rem;
}
</style>
